import { useState } from 'react';
import { DashboardLayout } from '@/components/Dashboard/DashboardLayout';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { useRPLStats, useEPIsExpiring, getEPICategoryLabel } from '@/hooks/useRPL';
import { EPIList } from '@/components/RPL/EPI/EPIList';
import { EPITypeFormDialog } from '@/components/RPL/EPI/EPITypeFormDialog';
import { EPIInventoryFormDialog } from '@/components/RPL/EPI/EPIInventoryFormDialog';
import { CertificationExpiryList } from '@/components/RPL/Training/CertificationExpiryList';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import {
  Shield,
  AlertTriangle,
  HardHat,
  GraduationCap,
  Calendar,
  Plus,
  ChevronRight,
  Clock,
  CheckCircle,
} from 'lucide-react';

export default function RPL() {
  const navigate = useNavigate();
  const { userRole } = useAuth();
  const { data: stats, isLoading: statsLoading } = useRPLStats();
  const { data: episExpiring } = useEPIsExpiring();
  const [epiTypeDialogOpen, setEpiTypeDialogOpen] = useState(false);
  const [epiInventoryDialogOpen, setEpiInventoryDialogOpen] = useState(false);

  const canManage = userRole === 'admin' || userRole === 'supervisor';

  const kpis = [
    {
      title: 'Dias sin accidentes graves',
      value: stats?.days_without_serious_incident || 0,
      icon: CheckCircle,
      color: 'text-green-500',
      bgColor: 'bg-green-500/10',
    },
    {
      title: 'Incidentes abiertos',
      value: stats?.open_incidents || 0,
      icon: AlertTriangle,
      color: stats?.open_incidents ? 'text-red-500' : 'text-green-500',
      bgColor: stats?.open_incidents ? 'bg-red-500/10' : 'bg-green-500/10',
    },
    {
      title: 'EPIs por caducar',
      value: stats?.epis_expiring_soon || 0,
      icon: HardHat,
      color: stats?.epis_expiring_soon ? 'text-yellow-500' : 'text-green-500',
      bgColor: stats?.epis_expiring_soon ? 'bg-yellow-500/10' : 'bg-green-500/10',
    },
    {
      title: 'Formaciones proximas',
      value: stats?.upcoming_trainings || 0,
      icon: GraduationCap,
      color: 'text-blue-500',
      bgColor: 'bg-blue-500/10',
    },
  ];

  return (
    <DashboardLayout>
      <div className="space-y-6 p-6">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-3xl font-bold bg-gradient-magma bg-clip-text text-transparent flex items-center gap-2">
              <Shield className="w-8 h-8 text-primary" />
              Prevencion de Riesgos Laborales
            </h1>
            <p className="text-muted-foreground mt-2">
              Gestion de EPIs, incidentes y formaciones de seguridad
            </p>
          </div>
          {canManage && (
            <div className="flex gap-2">
              <Button variant="outline" onClick={() => setEpiTypeDialogOpen(true)}>
                <Plus className="w-4 h-4 mr-2" />
                Tipo EPI
              </Button>
              <Button onClick={() => setEpiInventoryDialogOpen(true)}>
                <Plus className="w-4 h-4 mr-2" />
                Registrar EPI
              </Button>
            </div>
          )}
        </div>

        {/* KPIs */}
        <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-4">
          {kpis.map((kpi, index) => (
            <Card key={index}>
              <CardContent className="p-6">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm text-muted-foreground">{kpi.title}</p>
                    <p className={`text-3xl font-bold ${kpi.color}`}>{kpi.value}</p>
                  </div>
                  <div className={`p-3 rounded-full ${kpi.bgColor}`}>
                    <kpi.icon className={`w-6 h-6 ${kpi.color}`} />
                  </div>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>

        {/* Quick Access Cards */}
        <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
          <Card
            className="cursor-pointer hover:shadow-lg transition-shadow"
            onClick={() => navigate('/rpl/incidents')}
          >
            <CardContent className="p-6">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-4">
                  <div className="p-3 rounded-full bg-red-500/10">
                    <AlertTriangle className="w-6 h-6 text-red-500" />
                  </div>
                  <div>
                    <h3 className="font-semibold">Incidentes</h3>
                    <p className="text-sm text-muted-foreground">
                      {stats?.open_incidents || 0} abiertos / {stats?.total_incidents_year || 0} este ano
                    </p>
                  </div>
                </div>
                <ChevronRight className="w-5 h-5 text-muted-foreground" />
              </div>
            </CardContent>
          </Card>

          <Card
            className="cursor-pointer hover:shadow-lg transition-shadow"
            onClick={() => navigate('/rpl/training')}
          >
            <CardContent className="p-6">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-4">
                  <div className="p-3 rounded-full bg-blue-500/10">
                    <GraduationCap className="w-6 h-6 text-blue-500" />
                  </div>
                  <div>
                    <h3 className="font-semibold">Formaciones</h3>
                    <p className="text-sm text-muted-foreground">
                      {stats?.upcoming_trainings || 0} programadas
                    </p>
                  </div>
                </div>
                <ChevronRight className="w-5 h-5 text-muted-foreground" />
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardContent className="p-6">
              <div className="flex items-center gap-4">
                <div className="p-3 rounded-full bg-orange-500/10">
                  <Clock className="w-6 h-6 text-orange-500" />
                </div>
                <div>
                  <h3 className="font-semibold">Dias perdidos</h3>
                  <p className="text-sm text-muted-foreground">
                    {stats?.total_days_lost || 0} este ano
                  </p>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Main Content */}
        <div className="grid gap-6 lg:grid-cols-3">
          {/* EPIs Expiring */}
          <Card className="lg:col-span-1">
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <HardHat className="w-5 h-5" />
                EPIs por caducar
              </CardTitle>
            </CardHeader>
            <CardContent>
              {episExpiring?.length === 0 ? (
                <p className="text-sm text-muted-foreground text-center py-4">
                  No hay EPIs proximos a caducar
                </p>
              ) : (
                <div className="space-y-2">
                  {episExpiring?.slice(0, 5).map((epi) => (
                    <div
                      key={epi.id}
                      className={`flex items-center justify-between p-2 rounded border ${
                        epi.days_until_expiry <= 0
                          ? 'border-red-500 bg-red-500/10'
                          : epi.days_until_expiry <= 15
                          ? 'border-orange-500 bg-orange-500/10'
                          : 'border-yellow-500 bg-yellow-500/10'
                      }`}
                    >
                      <div className="min-w-0">
                        <p className="font-medium text-sm truncate">{epi.epi_type_name}</p>
                        <p className="text-xs text-muted-foreground truncate">{epi.employee_name}</p>
                      </div>
                      <Badge
                        variant="outline"
                        className={
                          epi.days_until_expiry <= 0
                            ? 'text-red-500'
                            : epi.days_until_expiry <= 15
                            ? 'text-orange-500'
                            : 'text-yellow-500'
                        }
                      >
                        {epi.days_until_expiry <= 0 ? 'Vencido' : `${epi.days_until_expiry}d`}
                      </Badge>
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>

          {/* Certifications Expiring */}
          <Card className="lg:col-span-2">
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <GraduationCap className="w-5 h-5" />
                Certificaciones por vencer
              </CardTitle>
            </CardHeader>
            <CardContent>
              <CertificationExpiryList limit={5} compact />
            </CardContent>
          </Card>
        </div>

        {/* EPIs Tab */}
        <Card>
          <CardHeader>
            <CardTitle>EPIs Asignados</CardTitle>
          </CardHeader>
          <CardContent>
            <EPIList />
          </CardContent>
        </Card>
      </div>

      <EPITypeFormDialog open={epiTypeDialogOpen} onOpenChange={setEpiTypeDialogOpen} />
      <EPIInventoryFormDialog open={epiInventoryDialogOpen} onOpenChange={setEpiInventoryDialogOpen} />
    </DashboardLayout>
  );
}
